/**
 * Every agent built on the template in the current workspace — the built-in
 * ones (AGENT_DEFINITIONS) and the custom ones administrators made
 * (workspace_custom_agents) — each with its settings row.
 *
 * Settings come from the shared agent settings store, so a save on an agent's
 * settings page shows here at once. Pass a behavior to get only agents of that
 * type (for example every agent that can estimate a value).
 */

import { useEffect, useMemo } from 'react';
import {
  AGENT_DEFINITIONS,
  definitionForCustomAgent,
  type AgentBehaviorKind,
  type AgentDefinition,
} from '../lib/ai/agentDefinitions';
import type { AgentSettings } from '../lib/agentSettingsService';
import { getCurrentWorkspaceId } from '../lib/supabaseSync';
import { useAgentSettingsStore } from '../stores/prioritization/agentSettingsStore';
import { useCustomAgentStore } from '../stores/prioritization/customAgentStore';

export interface TemplateAgent {
  def: AgentDefinition;
  settings: AgentSettings | null;
}

export function useTemplateAgents(behavior?: AgentBehaviorKind): TemplateAgent[] {
  const workspaceId = getCurrentWorkspaceId();
  const customAgents = useCustomAgentStore((s) => s.agents);
  const loadedWorkspaceId = useCustomAgentStore((s) => s.loadedWorkspaceId);
  const fetchAgents = useCustomAgentStore((s) => s.fetchAgents);
  const byAgent = useAgentSettingsStore((s) => s.byAgent);
  const fetchConfig = useAgentSettingsStore((s) => s.fetchConfig);

  useEffect(() => {
    if (workspaceId && loadedWorkspaceId !== workspaceId) void fetchAgents(workspaceId);
  }, [workspaceId, loadedWorkspaceId, fetchAgents]);

  const defs = useMemo(() => {
    const custom = loadedWorkspaceId === workspaceId ? customAgents.map(definitionForCustomAgent) : [];
    const all = [...Object.values(AGENT_DEFINITIONS), ...custom];
    return behavior ? all.filter((d) => d.behaviorKind === behavior) : all;
  }, [customAgents, loadedWorkspaceId, workspaceId, behavior]);

  useEffect(() => {
    if (!workspaceId) return;
    for (const def of defs) void fetchConfig(workspaceId, def.agentType);
  }, [workspaceId, defs, fetchConfig]);

  return useMemo(
    () => defs.map((def) => ({ def, settings: byAgent[def.agentType]?.config ?? null })),
    [defs, byAgent],
  );
}
